// The "Expires ..." line on the detail of a proposal that still waits for a
// decision. A proposal that is decided, or that carries no expiry (a client
// report), has no such line.

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface ExpiringProposal {
  status: string;
  expires_at?: string | null;
}

const PENDING_STATUSES = ['pending_review', 'proposed'];

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** When a waiting proposal expires; null when it has no expiry to show. */
export function proposalExpiryDate(proposal: ExpiringProposal): Date | null {
  if (!PENDING_STATUSES.includes(proposal.status)) return null;
  if (!proposal.expires_at) return null;
  const date = new Date(proposal.expires_at);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "in 5 hours", rounded down to the largest unit that fits. */
export function expiresRelative(
  t: Translate,
  expiresAt: Date,
  now: number = Date.now()
): string {
  const left = expiresAt.getTime() - now;
  if (left < MINUTE) return t('ProposalExpiresSoon');
  if (left < HOUR) {
    return t('ProposalExpiresInMinutes', { count: Math.floor(left / MINUTE) });
  }
  if (left < 2 * DAY) {
    return t('ProposalExpiresInHours', { count: Math.floor(left / HOUR) });
  }
  return t('ProposalExpiresInDays', { count: Math.floor(left / DAY) });
}

/** "Expires in 5 hours (3 Oct 2026, 14:00)", or null when there is no line. */
export function proposalExpiryLine(
  t: Translate,
  proposal: ExpiringProposal,
  now: number = Date.now(),
  locale?: string
): string | null {
  const date = proposalExpiryDate(proposal);
  if (!date) return null;
  return t('ProposalExpiresLine', {
    relative: expiresRelative(t, date, now),
    date: date.toLocaleString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
  });
}
